'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Latches true the first time the element comes within `rootMargin` of the
 * viewport, then stops observing. For mounting something expensive (a WebGL
 * canvas, a heavy chunk) only once a visitor scrolls near it.
 *
 * Where IntersectionObserver doesn't exist it reports true straight away, so
 * the content still appears rather than waiting forever.
 */
export function useInViewOnce<T extends Element>(rootMargin = '200px') {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Deferred a frame, not set in the effect body (react-hooks/set-state-in-effect).
      const id = requestAnimationFrame(() => setInView(true));
      return () => cancelAnimationFrame(id);
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView] as const;
}
